import React, { useEffect, useMemo, useState } from "react";
import { Banknote, Building2, CreditCard, Euro, Eye, Mail, Printer, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty, RowSearch } from "@/pages/shared";
import StaffPicker from "@/components/StaffPicker";

const isUnpaid = (s) => s.payment === "account" && !s.paid_at;

export default function AdminSalesInvoices() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [cash, setCash] = useState(0);
  const [staff, setStaff] = useState(null);

  const load = async () => {
    try { setRows((await api.get("/sales")).data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    const filtered = rows.filter((r) => !s || [r.number, r.guest_name, r.pitch_label, r.bill_to?.name].some((v) => (v || "").toLowerCase().includes(s)));
    // Onbetaalde (op rekening) bovenaan, betaalde onderaan; binnen elke groep op datum (reeds gesorteerd).
    return [...filtered].sort((a, b) => (isUnpaid(a) ? 0 : 1) - (isUnpaid(b) ? 0 : 1));
  }, [rows, q]);

  const firstPaidIdx = visible.findIndex((s) => !isUnpaid(s));
  const unpaidCount = visible.filter(isUnpaid).length;

  const total = payFor?.total || 0;
  const payCard = Math.round((total - cash) * 100) / 100;
  const setCashSafe = (v) => { const n = Math.max(0, Math.min(total, Number(v) || 0)); setCash(Math.round(n * 100) / 100); };
  const openPay = (s) => { setPayFor(s); setCash(s.total || 0); setStaff(null); };
  const confirmPay = async () => {
    try {
      await api.post(`/sales/${payFor.id}/pay`, { cash, card: payCard, staff_id: staff?.id || null });
      toast.success(t("pay_registered"));
      setPayFor(null);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const sum = visible.reduce((a, r) => a + (r.total || 0), 0);

  const print = (s) => {
    setView(null);
    setTimeout(() => window.open(`/print-verkoop?sale=${s.id}`, "_blank"), 150);
    setTimeout(load, 2500);
  };

  const email = async (s) => {
    if (!s.guest_id) return toast.error(t("no_email_print"));
    try {
      const { data } = await api.post(`/sales/${s.id}/email`);
      await load();
      toast.success(`${t("email_sent")} ${data.to}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Panel
      title={`${t("admin_sales_inv")} (${visible.length}${q ? `/${rows.length}` : ""})`}
      testid="sales-invoices-panel"
      action={<RowSearch value={q} onChange={setQ} placeholder={t("invoice_number")} testid="sales-invoices-search" />}
    >
      <div className="px-5 pt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">{t("admin_sales_desc")}</span>
        <span className="text-muted-foreground">{t("total")}: <span className="font-display font-extrabold text-primary tabular-nums" data-testid="sales-invoices-sum">{money(sum)}</span></span>
      </div>
      {visible.length === 0 ? <Empty /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("invoice_number")}</TableHead>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("bill_to")}</TableHead>
              <TableHead>{t("pitch")}</TableHead>
              <TableHead>{t("sale_billing")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead className="text-right">{t("subtotal")}</TableHead>
              <TableHead className="text-right">{t("vat")}</TableHead>
              <TableHead className="text-right">{t("total")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((s, idx) => (
              <React.Fragment key={s.id}>
                {idx === firstPaidIdx && firstPaidIdx > 0 && (
                  <TableRow data-testid="paid-group-separator" className="bg-muted/40 hover:bg-muted/40">
                    <TableCell colSpan={10} className="py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("inv_paid")}</TableCell>
                  </TableRow>
                )}
                {idx === 0 && unpaidCount > 0 && (
                  <TableRow data-testid="unpaid-group-separator" className="bg-amber-50/60 dark:bg-amber-950/20 hover:bg-amber-50/60">
                    <TableCell colSpan={10} className="py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">{t("inv_unpaid")} ({unpaidCount})</TableCell>
                  </TableRow>
                )}
                <TableRow data-testid={`sales-invoice-row-${s.id}`} className={isUnpaid(s) ? "bg-amber-50/30 dark:bg-amber-950/10" : ""}>
                  <TableCell className="font-semibold text-primary tabular-nums">{s.number || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{fmtDate(s.created_at)}</TableCell>
                  <TableCell className="font-semibold text-foreground">{s.bill_to?.name || s.guest_name}</TableCell>
                  <TableCell className="text-xs font-bold text-primary">{s.pitch_label || "—"}</TableCell>
                  <TableCell className="text-xs">
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      {s.billing === "company" ? <Building2 className="h-3 w-3" /> : <UserRound className="h-3 w-3" />}
                      {t(s.billing === "company" ? "sale_company" : "sale_private")}
                    </span>
                  </TableCell>
                  <TableCell>
                    {isUnpaid(s)
                      ? <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-semibold" data-testid={`inv-status-${s.id}`}>{t("inv_unpaid")}</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`inv-status-${s.id}`}>{t("inv_paid")}{s.paid_method ? ` · ${t(`pay_m_${s.paid_method}`)}` : ""}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{money(s.subtotal)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{money(s.vat_amount)}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(s.total)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {isUnpaid(s) && <Button variant="ghost" size="sm" className="text-emerald-700" onClick={() => openPay(s)} title={t("pay")} data-testid={`pay-sales-invoice-${s.id}`}><Euro className="h-4 w-4" /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => setView(s)} data-testid={`view-sales-invoice-${s.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => print(s)} data-testid={`print-sales-invoice-${s.id}`}><Printer className="h-4 w-4" /></Button>
                    {s.guest_id && <Button variant="ghost" size="sm" onClick={() => email(s)} title={t("email_invoice")} data-testid={`email-sales-invoice-${s.id}`}><Mail className={`h-4 w-4 ${s.emailed_at ? "text-emerald-600" : ""}`} /></Button>}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-lg" data-testid="sales-invoice-view">
          <DialogHeader><DialogTitle className="font-display">{t("invoice")} {view?.number}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-muted/50 p-3 text-xs">
                <p className="font-bold text-foreground text-sm">{view.bill_to?.name || view.guest_name}</p>
                <p>{[view.bill_to?.street, view.bill_to?.house_number].filter(Boolean).join(" ")}</p>
                <p>{[view.bill_to?.postal_code, view.bill_to?.city].filter(Boolean).join(" ")} {view.bill_to?.country}</p>
                {view.bill_to?.vat_number && <p>{t("setup_vat_number")}: {view.bill_to.vat_number}</p>}
                <p className="text-muted-foreground mt-1">{fmtDate(view.created_at)} · {view.user}</p>
              </div>
              <div className="border rounded-lg divide-y">
                {(view.lines || []).map((l, i) => (
                  <div key={i} className="flex justify-between px-3 py-2">
                    <span>{l.qty}× {l.description} <span className="text-muted-foreground text-xs">({l.vat_rate}%)</span></span>
                    <span className="tabular-nums">{money(l.total)}</span>
                  </div>
                ))}
              </div>
              <div className="text-right space-y-0.5">
                <p className="text-muted-foreground">{t("subtotal")}: {money(view.subtotal)}</p>
                <p className="text-muted-foreground">{t("vat")}: {money(view.vat_amount)}</p>
                <p className="font-display font-bold text-lg text-primary">{t("total")}: {money(view.total)}</p>
              </div>
              <Button className="w-full" onClick={() => print(view)} data-testid="view-print-sale"><Printer className="h-4 w-4 mr-1" />{t("print_invoice")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm" data-testid="pay-invoice-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("pay")} · {payFor?.number}</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-4 text-sm">
              <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
                <span className="text-muted-foreground">{t("total")}</span>
                <span className="font-display font-extrabold text-lg text-primary tabular-nums" data-testid="pay-total">{money(total)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={cash >= total ? "default" : "outline"} size="sm" onClick={() => setCashSafe(total)} data-testid="pay-all-cash"><Banknote className="h-4 w-4 mr-1" />{t("pay_m_cash")}</Button>
                <Button type="button" variant={payCard >= total ? "default" : "outline"} size="sm" onClick={() => setCashSafe(0)} data-testid="pay-all-card"><CreditCard className="h-4 w-4 mr-1" />{t("pay_m_card")}</Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Banknote className="h-3 w-3" />{t("pay_m_cash")}</span>
                  <Input type="number" min="0" step="0.01" value={cash} onChange={(e) => setCashSafe(e.target.value)} className="tabular-nums" data-testid="pay-cash-input" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><CreditCard className="h-3 w-3" />{t("pay_m_card")}</span>
                  <Input type="number" min="0" step="0.01" value={payCard} onChange={(e) => setCashSafe(total - (Number(e.target.value) || 0))} className="tabular-nums" data-testid="pay-card-input" />
                </label>
              </div>
              {cash > 0 && payCard > 0 && <p className="text-[11px] text-muted-foreground" data-testid="pay-split-note">{t("pay_split_note")}</p>}
              <StaffPicker staff={staff} setStaff={setStaff} />
              <Button className="w-full" onClick={confirmPay} data-testid="pay-confirm"><Euro className="h-4 w-4 mr-1" />{t("pay")} · {money(total)}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
